// =====================================================================
// Assessment Center — Instructor Panel
// Modal for picking published questions from the Question Bank when
// building an exam section. Filtered to the section's question type,
// since exam_sections are homogenous by design (see migrations/0001_initial_schema.sql).
// =====================================================================
import { useEffect, useState } from "react";
import { Badge, Button, Input, Modal, Spinner } from "../../../shared/components/ui";
import { listQuestions, type QuestionWithDetail } from "../lib/mock-api";
import type { QuestionType } from "../../../shared/types/db";

export default function QuestionPickerModal({
  open,
  sectionType,
  alreadySelectedIds,
  onClose,
  onAdd,
}: {
  open: boolean;
  sectionType: QuestionType;
  alreadySelectedIds: string[];
  onClose: () => void;
  onAdd: (questions: QuestionWithDetail[]) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<QuestionWithDetail[]>([]);
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setChecked(new Set());
    setSearch("");
    setLoading(true);
    listQuestions({ type: sectionType, status: "published" }).then((data) => {
      setItems(data);
      setLoading(false);
    });
  }, [open, sectionType]);

  const filtered = items.filter(
    (q) => !search || q.prompt.toLowerCase().includes(search.toLowerCase())
  );

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleAdd() {
    onAdd(items.filter((q) => checked.has(q.id)));
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Add ${sectionType === "essay" ? "essay" : "multiple choice"} questions`}
      widthClass="max-w-2xl"
    >
      <Input
        placeholder="Search published questions…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4"
      />

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-slate-500">
          <Spinner /> Loading question bank…
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-500">
          No published {sectionType === "essay" ? "essay" : "multiple choice"} questions found.
          Publish some from the Question Bank first.
        </p>
      ) : (
        <ul className="max-h-96 space-y-2 overflow-y-auto">
          {filtered.map((q) => {
            const already = alreadySelectedIds.includes(q.id);
            return (
              <li key={q.id}>
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border border-border-soft p-3 text-sm ${
                    already ? "opacity-40" : "hover:bg-navy-50/50"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-navy-700"
                    disabled={already}
                    checked={checked.has(q.id)}
                    onChange={() => toggle(q.id)}
                  />
                  <div className="flex-1">
                    <p className="text-slate-700">{q.prompt}</p>
                    <div className="mt-1 flex gap-2">
                      <Badge tone="neutral">{q.difficulty}</Badge>
                      <Badge tone="neutral">{q.default_points} pts</Badge>
                      {already && <Badge tone="navy">Already in section</Badge>}
                    </div>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-5 flex justify-end gap-2 border-t border-border-soft pt-4">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={checked.size === 0} onClick={handleAdd}>
          Add {checked.size > 0 ? checked.size : ""} question{checked.size === 1 ? "" : "s"}
        </Button>
      </div>
    </Modal>
  );
}
